import * as THREE from 'three';
import type { PlacedProp, RoomLayoutDef } from '../../types/game';

/* ------------------------------------------------------------------ */
/* Rejilla transitable + búsqueda de rutas                             */
/* ------------------------------------------------------------------ */

export interface WalkGrid {
  w: number;
  d: number;
  blocked: Uint8Array;
}

export function buildWalkGrid(room: RoomLayoutDef, solids: Array<{ x: number; z: number; w: number; d: number }>): WalkGrid {
  const w = room.tilesWide;
  const d = room.tilesDeep;
  const blocked = new Uint8Array(w * d);
  // El perímetro siempre es muro: nada de caerse del mundo.
  for (let x = 0; x < w; x++) { blocked[x] = 1; blocked[(d - 1) * w + x] = 1; }
  for (let z = 0; z < d; z++) { blocked[z * w] = 1; blocked[z * w + w - 1] = 1; }
  for (const s of solids) {
    const x0 = Math.max(1, Math.floor(s.x));
    const x1 = Math.min(w - 2, Math.ceil(s.x + s.w - 1));
    const z0 = Math.max(1, Math.floor(s.z));
    const z1 = Math.min(d - 2, Math.ceil(s.z + s.d - 1));
    for (let z = z0; z <= z1; z++) for (let x = x0; x <= x1; x++) blocked[z * w + x] = 1;
  }
  return { w, d, blocked };
}

export function isWalkable(g: WalkGrid, x: number, z: number): boolean {
  const ix = Math.round(x);
  const iz = Math.round(z);
  if (ix < 0 || iz < 0 || ix >= g.w || iz >= g.d) return false;
  return g.blocked[iz * g.w + ix] === 0;
}

export function nearestWalkable(g: WalkGrid, x: number, z: number, maxRing = 8): { x: number; z: number } {
  const ix = Math.round(x);
  const iz = Math.round(z);
  if (isWalkable(g, ix, iz)) return { x: ix, z: iz };
  for (let ring = 1; ring <= maxRing; ring++) {
    let best: { x: number; z: number } | null = null;
    let bestD = Infinity;
    for (let dz = -ring; dz <= ring; dz++) {
      for (let dx = -ring; dx <= ring; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== ring) continue;
        if (isWalkable(g, ix + dx, iz + dz)) {
          const dd = dx * dx + dz * dz;
          if (dd < bestD) { bestD = dd; best = { x: ix + dx, z: iz + dz }; }
        }
      }
    }
    if (best) return best;
  }
  return { x: ix, z: iz };
}

/** A* sobre la rejilla con movimiento en 4 direcciones. */
export function findPath(g: WalkGrid, sx: number, sz: number, tx: number, tz: number): Array<{ x: number; z: number }> | null {
  const start = nearestWalkable(g, sx, sz);
  const goal = nearestWalkable(g, tx, tz);
  const w = g.w;
  const d = g.d;
  const si = start.z * w + start.x;
  const gi = goal.z * w + goal.x;
  if (si === gi) return [goal];
  const open: number[] = [si];
  const came = new Int32Array(w * d).fill(-1);
  const gScore = new Float32Array(w * d).fill(Infinity);
  const fScore = new Float32Array(w * d).fill(Infinity);
  gScore[si] = 0;
  fScore[si] = Math.abs(start.x - goal.x) + Math.abs(start.z - goal.z);
  const closed = new Uint8Array(w * d);
  const dirs = [1, -1, w, -w];
  let guard = 0;
  while (open.length && guard++ < 20000) {
    let bi = 0;
    for (let i = 1; i < open.length; i++) if (fScore[open[i]] < fScore[open[bi]]) bi = i;
    const cur = open.splice(bi, 1)[0];
    if (cur === gi) {
      const path: Array<{ x: number; z: number }> = [];
      let n = cur;
      while (n !== -1) { path.unshift({ x: n % w, z: Math.floor(n / w) }); n = came[n]; }
      return path;
    }
    closed[cur] = 1;
    const cx = cur % w;
    for (const dd of dirs) {
      const nx = cur + dd;
      // Evita saltos de fila al sumar ±1 en los bordes.
      if (dd === 1 && cx === w - 1) continue;
      if (dd === -1 && cx === 0) continue;
      if (nx < 0 || nx >= w * d || g.blocked[nx] === 1 || closed[nx] === 1) continue;
      const tent = gScore[cur] + 1;
      if (tent < gScore[nx]) {
        came[nx] = cur;
        gScore[nx] = tent;
        fScore[nx] = tent + Math.abs((nx % w) - goal.x) + Math.abs(Math.floor(nx / w) - goal.z);
        if (!open.includes(nx)) open.push(nx);
      }
    }
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Paletas por tema: una familia de color coherente por sala           */
/* ------------------------------------------------------------------ */

export interface Palette {
  sky: number;
  fog: number;
  hemi: number;
  sun: number;
  sunIntensity: number;
  ground: number;
  groundAlt: number;
  path: number;
  wall: number;
  wood: number;
  accent: number;
  leaf: number;
  leafAlt: number;
  stone: number;
  metal: number;
  cloth: number;
  glow: number;
}

export const PALETTES: Record<string, Palette> = {
  plaza: {
    sky: 0x8ecae6, fog: 0xbfe3f2, hemi: 0xdff3ff, sun: 0xfff2cc, sunIntensity: 1.15,
    ground: 0x8fbf6b, groundAlt: 0x83b461, path: 0xd9c08f, wall: 0xb98d5e,
    wood: 0x9c6b3f, accent: 0xe76f51, leaf: 0x4f9d4f, leafAlt: 0x67b26f,
    stone: 0xb9bdc4, metal: 0x8d939c, cloth: 0x2a9d8f, glow: 0xffd479
  },
  patio: {
    sky: 0xf4a261, fog: 0xf7c59f, hemi: 0xffe8d1, sun: 0xffd9a0, sunIntensity: 1.0,
    ground: 0xcdb489, groundAlt: 0xc2a97e, path: 0xa98467, wall: 0xa9714b,
    wood: 0x8a5a34, accent: 0xe63946, leaf: 0x6a994e, leafAlt: 0x86b06b,
    stone: 0xc9c2b8, metal: 0x93897c, cloth: 0xbc4749, glow: 0xffb703
  },
  sala: {
    sky: 0x2b2d42, fog: 0x3a3d55, hemi: 0x8d99ae, sun: 0xffe6a7, sunIntensity: 0.85,
    ground: 0x8a6f4d, groundAlt: 0x7d6446, path: 0x9a7b5c, wall: 0x6d5a44,
    wood: 0x7f5539, accent: 0xef476f, leaf: 0x588157, leafAlt: 0x6a994e,
    stone: 0xa8a29a, metal: 0x7d8590, cloth: 0x118ab2, glow: 0xffd166
  },
  jardin: {
    sky: 0xa8dadc, fog: 0xcdeef0, hemi: 0xe8fbfb, sun: 0xfff8e1, sunIntensity: 1.1,
    ground: 0x74a575, groundAlt: 0x699a6a, path: 0xe0d3b6, wall: 0x7f9172,
    wood: 0x8b6f47, accent: 0xf4a261, leaf: 0x3f7d40, leafAlt: 0x5c9e5c,
    stone: 0xbfc5bd, metal: 0x8a9188, cloth: 0x457b9d, glow: 0xffe08a
  },
  noche: {
    sky: 0x101426, fog: 0x161b30, hemi: 0x3a4a6b, sun: 0x9fb8ff, sunIntensity: 0.45,
    ground: 0x3c4a3e, groundAlt: 0x35423a, path: 0x6b6f7a, wall: 0x4a4438,
    wood: 0x5c4630, accent: 0xe63946, leaf: 0x2f4f30, leafAlt: 0x3a5c3a,
    stone: 0x6f7480, metal: 0x5c6270, cloth: 0x4361ee, glow: 0xffc94d
  }
};

export function paletteFor(theme: string | undefined, kind: string): Palette {
  if (theme && PALETTES[theme]) return PALETTES[theme];
  if (kind === 'sala' || kind === 'corredor') return PALETTES.sala;
  if (kind === 'jardin') return PALETTES.jardin;
  if (kind === 'patio') return PALETTES.patio;
  return PALETTES.plaza;
}

/* ------------------------------------------------------------------ */
/* Utilidades de material y geometría blocky                           */
/* ------------------------------------------------------------------ */

const matCache = new Map<number, THREE.MeshLambertMaterial>();
export function mat(color: number): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = new THREE.MeshLambertMaterial({ color }); matCache.set(color, m); }
  return m;
}

function box(w: number, h: number, d: number, color: number, x = 0, y = 0, z = 0): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function cyl(rt: number, rb: number, h: number, color: number, x = 0, y = 0, z = 0, seg = 8): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(rt, rb, h, seg), mat(color));
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

function cone(r: number, h: number, color: number, x = 0, y = 0, z = 0, seg = 7): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.ConeGeometry(r, h, seg), mat(color));
  m.position.set(x, y, z);
  m.castShadow = true;
  return m;
}

/** Huella de colisión por rol de prop, en tiles. */
export function propFootprint(role: string): { w: number; d: number } {
  switch (role) {
    case 'tree': case 'pine': case 'statue': case 'fountain': case 'stage': case 'gate':
    case 'car': case 'bus': case 'truck':
      return { w: 2, d: 2 };
    case 'bookshelf': case 'shelf': case 'bench': case 'hedge': case 'fence': case 'wall': case 'sign': case 'noticeboard': case 'podium': case 'desk': case 'table': case 'bed': case 'counter':
      return { w: 2, d: 1 };
    default:
      return { w: 1, d: 1 };
  }
}

/* ------------------------------------------------------------------ */
/* Props blocky por rol                                                */
/* ------------------------------------------------------------------ */

export function buildProp(role: string, p: Palette, tint = 0): THREE.Group {
  const g = new THREE.Group();
  const leaf = tint % 2 === 1 ? p.leafAlt : p.leaf;
  switch (role) {
    case 'tree':
      g.add(cyl(0.14, 0.2, 1.0, p.wood, 0, 0.5, 0));
      g.add(cone(0.85, 1.5, leaf, 0, 1.7, 0, 8));
      g.add(cone(0.6, 1.0, leaf, 0, 2.4, 0, 8));
      break;
    case 'pine':
      g.add(cyl(0.12, 0.16, 0.7, p.wood, 0, 0.35, 0));
      g.add(cone(0.7, 1.2, leaf, 0, 1.3, 0, 6));
      g.add(cone(0.5, 0.9, leaf, 0, 2.0, 0, 6));
      g.add(cone(0.32, 0.7, leaf, 0, 2.6, 0, 6));
      break;
    case 'bush':
      g.add(box(0.7, 0.5, 0.7, leaf, 0, 0.25, 0));
      g.add(box(0.4, 0.3, 0.4, p.leafAlt, 0.15, 0.55, 0.1));
      break;
    case 'flower':
      g.add(cyl(0.03, 0.03, 0.35, p.leaf, 0, 0.17, 0, 5));
      g.add(box(0.18, 0.12, 0.18, p.accent, 0, 0.4, 0));
      break;
    case 'rock':
      g.add(box(0.6, 0.4, 0.5, p.stone, 0, 0.2, 0));
      g.add(box(0.3, 0.25, 0.3, p.stone, 0.2, 0.45, 0.1));
      break;
    case 'bench':
      g.add(box(1.6, 0.1, 0.5, p.wood, 0, 0.45, 0));
      g.add(box(1.6, 0.4, 0.1, p.wood, 0, 0.7, -0.22));
      g.add(box(0.12, 0.45, 0.4, p.wood, -0.7, 0.22, 0));
      g.add(box(0.12, 0.45, 0.4, p.wood, 0.7, 0.22, 0));
      break;
    case 'lamp':
      g.add(cyl(0.06, 0.1, 1.6, p.metal, 0, 0.8, 0, 6));
      g.add(box(0.3, 0.3, 0.3, p.glow, 0, 1.7, 0));
      break;
    case 'streetlamp':
      g.add(cyl(0.08, 0.14, 2.4, p.metal, 0, 1.2, 0, 6));
      g.add(box(0.34, 0.34, 0.34, p.glow, 0, 2.5, 0));
      g.add(cone(0.3, 0.3, p.metal, 0, 2.8, 0, 4));
      break;
    case 'desk': case 'table':
      g.add(box(1.6, 0.12, 0.9, p.wood, 0, 0.75, 0));
      g.add(box(0.14, 0.75, 0.14, p.wood, -0.7, 0.37, -0.35));
      g.add(box(0.14, 0.75, 0.14, p.wood, 0.7, 0.37, -0.35));
      g.add(box(0.14, 0.75, 0.14, p.wood, -0.7, 0.37, 0.35));
      g.add(box(0.14, 0.75, 0.14, p.wood, 0.7, 0.37, 0.35));
      g.add(box(0.5, 0.06, 0.35, 0xf5f0e6, -0.3, 0.84, 0)); // papel encima
      break;
    case 'bookshelf': case 'shelf':
      g.add(box(1.6, 1.8, 0.5, p.wood, 0, 0.9, 0));
      for (let i = 0; i < 3; i++) {
        g.add(box(1.4, 0.3, 0.34, i % 2 ? p.cloth : p.accent, 0, 0.5 + i * 0.5, 0.1));
      }
      break;
    case 'podium':
      g.add(box(0.9, 1.1, 0.7, p.wood, 0, 0.55, 0));
      g.add(box(1.0, 0.12, 0.8, p.wood, 0, 1.15, 0));
      g.add(box(0.5, 0.05, 0.35, 0xf5f0e6, 0, 1.24, 0));
      break;
    case 'sign':
      g.add(box(0.12, 1.2, 0.12, p.wood, 0, 0.6, 0));
      g.add(box(1.1, 0.6, 0.1, p.wood, 0, 1.4, 0));
      g.add(box(0.8, 0.1, 0.06, p.cloth, 0, 1.5, 0.07));
      g.add(box(0.6, 0.1, 0.06, p.cloth, 0, 1.3, 0.07));
      break;
    case 'noticeboard':
      g.add(box(0.1, 1.3, 0.1, p.wood, -0.6, 0.65, 0));
      g.add(box(0.1, 1.3, 0.1, p.wood, 0.6, 0.65, 0));
      g.add(box(1.5, 1.0, 0.12, p.wood, 0, 1.5, 0));
      g.add(box(0.3, 0.4, 0.05, 0xf5f0e6, -0.4, 1.6, 0.09));
      g.add(box(0.3, 0.4, 0.05, p.accent, 0.1, 1.45, 0.09));
      g.add(box(0.3, 0.4, 0.05, 0xf5f0e6, 0.45, 1.62, 0.09));
      break;
    case 'fountain':
      g.add(cyl(1.3, 1.4, 0.4, p.stone, 0, 0.2, 0, 12));
      g.add(cyl(1.0, 1.0, 0.25, 0x4fa3d1, 0, 0.42, 0, 12));
      g.add(cyl(0.2, 0.3, 0.9, p.stone, 0, 0.7, 0, 8));
      g.add(cyl(0.5, 0.5, 0.15, p.stone, 0, 1.2, 0, 10));
      g.add(cyl(0.12, 0.12, 0.4, 0x7cc4e8, 0, 1.4, 0, 6));
      break;
    case 'statue':
      g.add(box(1.2, 0.5, 1.2, p.stone, 0, 0.25, 0));
      g.add(box(0.6, 1.2, 0.5, p.stone, 0, 1.1, 0));
      g.add(box(0.5, 0.5, 0.5, p.stone, 0, 1.95, 0));
      g.add(box(0.16, 0.7, 0.16, p.stone, 0.42, 1.4, 0.1));
      break;
    case 'stage':
      g.add(box(3.0, 0.4, 2.0, p.wood, 0, 0.2, 0));
      g.add(box(3.0, 0.1, 0.3, p.accent, 0, 0.45, 0.9));
      g.add(box(0.2, 2.2, 0.2, p.wood, -1.4, 1.1, -0.9));
      g.add(box(0.2, 2.2, 0.2, p.wood, 1.4, 1.1, -0.9));
      g.add(box(3.0, 0.2, 0.2, p.wood, 0, 2.2, -0.9));
      g.add(box(2.8, 0.9, 0.08, p.cloth, 0, 1.7, -0.95));
      break;
    case 'crate':
      g.add(box(0.7, 0.7, 0.7, p.wood, 0, 0.35, 0));
      g.add(box(0.74, 0.1, 0.74, p.wood, 0, 0.7, 0));
      break;
    case 'barrel':
      g.add(cyl(0.35, 0.35, 0.8, p.wood, 0, 0.4, 0, 10));
      g.add(cyl(0.37, 0.37, 0.1, p.metal, 0, 0.6, 0, 10));
      break;
    case 'plant':
      g.add(cyl(0.25, 0.2, 0.4, p.accent, 0, 0.2, 0, 8));
      g.add(cone(0.35, 0.7, leaf, 0, 0.7, 0, 7));
      break;
    case 'chair':
      g.add(box(0.5, 0.1, 0.5, p.wood, 0, 0.45, 0));
      g.add(box(0.5, 0.6, 0.1, p.wood, 0, 0.75, -0.22));
      g.add(box(0.08, 0.45, 0.08, p.wood, -0.2, 0.22, -0.2));
      g.add(box(0.08, 0.45, 0.08, p.wood, 0.2, 0.22, -0.2));
      g.add(box(0.08, 0.45, 0.08, p.wood, -0.2, 0.22, 0.2));
      g.add(box(0.08, 0.45, 0.08, p.wood, 0.2, 0.22, 0.2));
      break;
    case 'bed':
      g.add(box(1.6, 0.4, 0.9, p.wood, 0, 0.2, 0));
      g.add(box(1.5, 0.2, 0.8, p.cloth, 0, 0.5, 0));
      g.add(box(0.4, 0.16, 0.7, 0xf5f0e6, -0.55, 0.62, 0));
      break;
    case 'counter':
      g.add(box(1.8, 0.9, 0.7, p.wood, 0, 0.45, 0));
      g.add(box(1.9, 0.1, 0.8, p.stone, 0, 0.95, 0));
      break;
    case 'computer':
      g.add(box(0.6, 0.4, 0.1, 0x2b2d42, 0, 1.0, 0));
      g.add(box(0.5, 0.3, 0.05, 0x7cc4e8, 0, 1.0, 0.06));
      g.add(box(0.4, 0.06, 0.25, 0x2b2d42, 0, 0.84, 0.15));
      break;
    case 'hedge': case 'fence': case 'wall':
      g.add(box(1.0, role === 'wall' ? 1.6 : 0.9, 0.4, role === 'hedge' ? leaf : p.wood, 0, (role === 'wall' ? 1.6 : 0.9) / 2, 0));
      break;
    case 'gate':
      g.add(box(0.3, 2.0, 0.3, p.stone, -0.9, 1.0, 0));
      g.add(box(0.3, 2.0, 0.3, p.stone, 0.9, 1.0, 0));
      g.add(box(2.1, 0.3, 0.3, p.stone, 0, 2.1, 0));
      g.add(box(1.5, 1.4, 0.12, p.metal, 0, 0.8, 0));
      break;
    case 'flag':
      g.add(cyl(0.05, 0.08, 2.6, p.metal, 0, 1.3, 0, 6));
      g.add(box(0.9, 0.5, 0.06, p.accent, 0.5, 2.3, 0));
      break;
    case 'rug':
      g.add(box(2.0, 0.04, 1.4, p.cloth, 0, 0.02, 0));
      g.add(box(1.6, 0.05, 1.0, p.accent, 0, 0.03, 0));
      break;
    default:
      g.add(box(0.6, 0.6, 0.6, p.stone, 0, 0.3, 0));
  }
  return g;
}

/* ------------------------------------------------------------------ */
/* Personaje blocky estilo Roblox con ciclo de caminado procedural     */
/* ------------------------------------------------------------------ */

export interface BlockyRig {
  group: THREE.Group;
  parts: {
    legL: THREE.Mesh; legR: THREE.Mesh; armL: THREE.Mesh; armR: THREE.Mesh;
    torso: THREE.Mesh; head: THREE.Mesh;
  };
  phase: number;
}

export function buildAvatar(body: number, skin: number, shirt: number): BlockyRig {
  const group = new THREE.Group();
  const legL = box(0.22, 0.5, 0.22, body, -0.13, 0.25, 0);
  const legR = box(0.22, 0.5, 0.22, body, 0.13, 0.25, 0);
  const torso = box(0.5, 0.55, 0.28, shirt, 0, 0.78, 0);
  const armL = box(0.16, 0.5, 0.2, shirt, -0.35, 0.8, 0);
  const armR = box(0.16, 0.5, 0.2, shirt, 0.35, 0.8, 0);
  const head = box(0.42, 0.4, 0.42, skin, 0, 1.28, 0);
  // Pivote de piernas y brazos en la cadera/hombro para que el balanceo se vea natural.
  for (const [mesh, pivotY] of [[legL, 0.5], [legR, 0.5], [armL, 1.05], [armR, 1.05]] as Array<[THREE.Mesh, number]>) {
    mesh.geometry.translate(0, -(pivotY - mesh.position.y), 0);
    mesh.position.y = pivotY;
  }
  head.geometry.translate(0, 0, 0);
  group.add(legL, legR, torso, armL, armR, head);
  group.traverse((o) => { o.castShadow = true; });
  return { group, parts: { legL, legR, armL, armR, torso, head }, phase: 0 };
}

/** Avanza el ciclo de caminado; `speed` 0 = reposo. */
export function animateRig(rig: BlockyRig, dt: number, speed: number, moving: boolean): void {
  if (moving && speed > 0.01) {
    rig.phase += dt * 9;
    const s = Math.sin(rig.phase);
    rig.parts.legL.rotation.x = s * 0.7;
    rig.parts.legR.rotation.x = -s * 0.7;
    rig.parts.armL.rotation.x = -s * 0.5;
    rig.parts.armR.rotation.x = s * 0.5;
    rig.parts.torso.position.y = 0.78 + Math.abs(Math.cos(rig.phase)) * 0.04;
  } else {
    rig.phase = 0;
    const damp = (v: THREE.Mesh) => { v.rotation.x += (0 - v.rotation.x) * Math.min(1, dt * 12); };
    damp(rig.parts.legL); damp(rig.parts.legR); damp(rig.parts.armL); damp(rig.parts.armR);
    rig.parts.torso.position.y += (0.78 + Math.sin(performance.now() / 600) * 0.015 - rig.parts.torso.position.y) * Math.min(1, dt * 8);
  }
}

/** Compañero flotante: un pequeño dron que sigue al jugador. */
export function buildCompanion(color: number, glow: number): THREE.Group {
  const g = new THREE.Group();
  g.add(box(0.34, 0.3, 0.34, color, 0, 0, 0));
  g.add(box(0.16, 0.12, 0.06, glow, 0, 0.02, 0.18));
  g.add(cyl(0.02, 0.02, 0.25, color, 0, 0.26, 0, 5));
  g.add(box(0.1, 0.06, 0.1, glow, 0, 0.4, 0));
  g.traverse((o) => { o.castShadow = true; });
  return g;
}

export function groundColorFor(role: string, p: Palette): number {
  switch (role) {
    case 'wood': return p.ground;
    case 'stone': return p.stone;
    case 'sand': return p.path;
    default: return p.ground;
  }
}

export function propFromPlaced(pp: PlacedProp, p: Palette): THREE.Group {
  return buildProp(pp.role, p, pp.tint ?? 0);
}
